/**
 * The "What's new" release modal (ported from marq-los-frontend): a hero
 * naming the release, up to four highlight rows, and a way to the full
 * changelog. Spacing and the slate type colors match Marq's modal; the accent
 * is Eastview teal and the hero has no icon tile. Super admins only for now
 * (see WhatsNewProvider), and the hero says so under the intro so nobody
 * mistakes it for something every user sees.
 *
 * Not built on QuickEditDialog / ConfirmationDialog on purpose: it confirms
 * and collects nothing, and its layout (tinted hero, clickable rows, hint +
 * two-button foot) is its own spec. MUI's Dialog still owns the focus trap,
 * Esc, the backdrop click, restoring focus on close, and role/aria-modal.
 *
 * A row or View all updates closes the modal and opens the Changelog page at
 * this release. Every way out marks the release seen (the provider's onClose).
 */
import React from "react";
import PropTypes from "prop-types";
import { keyframes } from "@emotion/react";
import { Box, Dialog } from "@mui/material";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";

import { changeKind, slate, teal } from "../../theme/colors";

const TITLE_ID = "whats-new-title";
const COMPACT = "@media (max-width: 760px)";
const NARROW = "@media (max-width: 420px)";
const REDUCED = "@media (prefers-reduced-motion: reduce)";
const CHANGELOG_PATH = "/changelog";

const TYPES = {
  feature: { pill: "New", ...changeKind.new },
  improvement: { pill: "Improved", ...changeKind.improved },
  fix: { pill: "Fixed", ...changeKind.fixed },
};
const typeOf = (key) => TYPES[key] || TYPES.improvement;
const TYPE_RANK = { feature: 0, improvement: 1, fix: 2 };

const enter = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(.98); }
  to   { opacity: 1; transform: none; }
`;

const safeDate = (iso) => {
  if (!iso) return null;
  try {
    const d = parseISO(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

// Release dates are local deploy times with no zone ("2026-10-15T18:00:00"),
// a calendar day rather than an instant, so they are formatted as written.
const releaseDate = (rel, pattern) => {
  const d = safeDate(rel?.date);
  return d ? format(d, pattern) : rel?.date || "";
};

// Same id the Changelog page links releases by: #10-15-2026.
export const releaseId = (rel) => {
  const d = safeDate(rel?.date);
  return d ? format(d, "MM-dd-yyyy") : String(rel?.version || "");
};

// The most rows the modal shows.
const MAX_ROWS = 5;
// More rows than this and the release is summarized by area instead of
// listed item by item.
const MAX_DETAILED = 5;

const byRank = (a, b) =>
  (TYPE_RANK[a.type] ?? 3) - (TYPE_RANK[b.type] ?? 3) || a.i - b.i;

// Highlights are read at a glance: one sentence, never a paragraph.
const firstSentence = (s) => {
  const m = String(s || "").match(/^.*?[.!?](?=s|$)/);
  return (m ? m[0] : String(s || "")).trim();
};

const listWords = (items) => {
  if (items.length <= 1) return items[0] || "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};

/**
 * The rows the modal leads with, at most five, New before Improved (fixes stay
 * on the page).
 *   1. A release's own `highlights` ({ type, title, summary }) win: written by
 *      hand, high level.
 *   2. A small release (five feature labels or fewer) lists those labels.
 *   3. A bigger one rolls up to one general row per portal section, naming
 *      the screens it touched, so the modal never reads like a diff.
 */
export const releaseHighlights = (rel) => {
  if (Array.isArray(rel?.highlights) && rel.highlights.length) {
    return rel.highlights
      .filter((h) => h.title)
      .map((h, i) => ({
        key: `h${i}`,
        i,
        type: h.type || "feature",
        title: h.title,
        summary: firstSentence(h.summary),
      }))
      .slice(0, MAX_ROWS)
      .sort(byRank);
  }

  const changes = (rel?.visibleChanges || rel?.changes || []).filter(
    (c) => c.type !== "fix" && (c.feature || c.title),
  );
  const seen = new Set();
  const detailed = [];
  changes.forEach((c, i) => {
    const title = c.feature || c.title;
    const key = [c.type, c.section, c.area, title].join("::");
    if (seen.has(key)) return;
    seen.add(key);
    detailed.push({ key, i, type: c.type, title, summary: firstSentence(c.description) });
  });
  if (detailed.length <= MAX_DETAILED) return detailed.slice(0, MAX_ROWS).sort(byRank);

  const sections = new Map();
  changes.forEach((c, i) => {
    const name = c.section || "Other updates";
    if (!sections.has(name)) sections.set(name, { i, areas: [], hasNew: false });
    const s = sections.get(name);
    if (c.area && c.area !== name && !s.areas.includes(c.area)) s.areas.push(c.area);
    if (c.type === "feature") s.hasNew = true;
  });
  return [...sections.entries()]
    .map(([name, s]) => {
      const shown = s.areas.slice(0, 3);
      const list =
        s.areas.length > shown.length
          ? `${shown.join(", ")} and more`
          : listWords(shown);
      return {
        key: name,
        i: s.i,
        type: s.hasNew ? "feature" : "improvement",
        title: name,
        summary: list ? `Updates to ${list}.` : "Several updates.",
      };
    })
    .slice(0, MAX_ROWS)
    .sort(byRank);
};

const WhatsNewModal = ({ open, release, onClose, onExited }) => {
  const navigate = useNavigate();
  const accent = teal[600];
  const focusRing = { outline: `2px solid ${teal[300]}`, outlineOffset: 2 };

  const highlights = releaseHighlights(release);
  const eyebrow = release.version
    ? `Version ${release.version}`
    : releaseDate(release, "MMM d, yyyy");
  const intro =
    release.summary ||
    "We've shipped a few things to save your team time. Here's what changed.";

  const goToChangelog = () => {
    onClose();
    navigate(`${CHANGELOG_PATH}#${releaseId(release)}`);
    window.scrollTo(0, 0);
  };

  const btnSx = {
    all: "unset",
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    height: 34,
    px: "14px",
    borderRadius: "8px",
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: "nowrap",
    cursor: "pointer",
    transition: "background-color .15s, color .15s",
    "&:focus-visible": focusRing,
    [NARROW]: { flex: 1 },
    [REDUCED]: { transition: "none" },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionProps={{ onExited }}
      aria-labelledby={TITLE_ID}
      transitionDuration={{ enter: 200, exit: 150 }}
      slotProps={{ backdrop: { sx: { bgcolor: "rgba(17,24,39,.38)" } } }}
      PaperProps={{
        sx: {
          m: 0,
          width: "min(520px, calc(100% - 32px))",
          maxWidth: "none",
          maxHeight: "calc(100% - 40px)",
          overflow: "auto",
          borderRadius: "14px",
          boxShadow:
            "0 20px 40px -12px rgba(16,24,40,.22), 0 4px 10px -4px rgba(16,24,40,.08)",
          color: slate[900],
          animation: `${enter} .25s cubic-bezier(.2,.8,.2,1)`,
          [REDUCED]: { animation: "none" },
        },
      }}
    >
      {/* hero */}
      <Box
        sx={{
          position: "relative",
          p: "28px 28px 20px",
          background: `linear-gradient(180deg, ${teal[50]} 0%, #FFFFFF 100%)`,
          borderBottom: `1px solid ${slate[200]}`,
          [COMPACT]: { p: "24px 20px 16px" },
        }}
      >
        <Box
          component="button"
          type="button"
          aria-label="Close"
          onClick={onClose}
          sx={{
            all: "unset",
            position: "absolute",
            top: 14,
            right: 14,
            width: 32,
            height: 32,
            borderRadius: "8px",
            display: "grid",
            placeItems: "center",
            color: slate[500],
            cursor: "pointer",
            "&:hover": { bgcolor: slate[50], color: slate[900] },
            "&:focus-visible": focusRing,
          }}
        >
          <CloseRounded sx={{ fontSize: 18 }} />
        </Box>
        <Box
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: teal[700],
            letterSpacing: ".02em",
            textTransform: "uppercase",
          }}
        >
          {eyebrow}
        </Box>
        <Box
          component="h2"
          id={TITLE_ID}
          sx={{
            fontSize: 20,
            fontWeight: 600,
            m: "10px 0 6px",
            letterSpacing: "-.01em",
            pr: 4,
          }}
        >
          Admin Deployment Review
        </Box>
        <Box sx={{ color: slate[500], fontSize: 13.5, lineHeight: 1.5 }}>
          {intro}
        </Box>
        {/* Super-admin notice, in the intro's own type so the hero keeps
            Marq's rhythm. */}
        <Box
          role="note"
          sx={{ color: slate[500], fontSize: 13.5, lineHeight: 1.5, mt: "6px" }}
        >
          <Box component="strong" sx={{ color: slate[900], fontWeight: 600 }}>
            Only super admins see this review.
          </Box>{" "}
          Other users don&apos;t get it.
        </Box>
      </Box>

      {/* highlights */}
      {highlights.length ? (
        <Box sx={{ p: "12px" }}>
          {highlights.map((h) => {
            const t = typeOf(h.type);
            return (
              <Box
                key={h.key}
                component="button"
                type="button"
                onClick={goToChangelog}
                sx={{
                  all: "unset",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "14px",
                  width: "100%",
                  p: "12px",
                  borderRadius: "10px",
                  cursor: "pointer",
                  transition: "background-color .12s",
                  "&:hover": { bgcolor: slate[50] },
                  "&:hover .go": { opacity: 1, transform: "none" },
                  "&:focus-visible": focusRing,
                  [REDUCED]: { transition: "none" },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 32,
                    mt: "2px",
                    height: 32,
                    borderRadius: "8px",
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    bgcolor: t.bg,
                    color: t.fg,
                  }}
                >
                  <AutoAwesomeOutlined sx={{ fontSize: 16 }} />
                </Box>
                <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    component="span"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "8px",
                      fontSize: 13.5,
                      fontWeight: 600,
                    }}
                  >
                    {h.title}
                    <Box
                      component="span"
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        px: "4px",
                        borderRadius: "5px",
                        bgcolor: t.bg,
                        color: t.fg,
                      }}
                    >
                      {t.pill}
                    </Box>
                  </Box>
                  {h.summary ? (
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        color: slate[500],
                        fontSize: 13,
                        mt: "2px",
                        lineHeight: 1.5,
                      }}
                    >
                      {h.summary}
                    </Box>
                  ) : null}
                </Box>
                <ArrowForwardRounded
                  className="go"
                  sx={{
                    ml: "auto",
                    mt: 1,
                    fontSize: 16,
                    flexShrink: 0,
                    color: slate[400],
                    opacity: 0,
                    transform: "translateX(-4px)",
                    transition: "opacity .15s, transform .15s",
                    [REDUCED]: { transition: "none" },
                  }}
                />
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{ height: 12 }} />
      )}

      {/* foot */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
          p: "14px 20px",
          borderTop: `1px solid ${slate[200]}`,
        }}
      >
        <Box
          component="small"
          sx={{ color: slate[500], fontSize: 12, [COMPACT]: { display: "none" } }}
        >
          {highlights.length ? "Click any item for full details" : ""}
        </Box>
        <Box sx={{ display: "flex", gap: "8px", [NARROW]: { flex: 1 } }}>
          <Box
            component="button"
            type="button"
            onClick={onClose}
            sx={{
              ...btnSx,
              color: slate[500],
              "&:hover": { bgcolor: slate[50], color: slate[900] },
            }}
          >
            Dismiss
          </Box>
          <Box
            component="button"
            type="button"
            onClick={goToChangelog}
            sx={{
              ...btnSx,
              bgcolor: accent,
              color: "#FFFFFF",
              "&:hover": { bgcolor: teal[700] },
            }}
          >
            View all updates
            <ArrowForwardRounded sx={{ fontSize: 16 }} />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

WhatsNewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  release: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onExited: PropTypes.func,
};

export default WhatsNewModal;
